import { useEffect, useMemo, useState } from 'react';
import { Typography } from '../../../../core/components/molecules/Typography';
import MemoryCard from '../card';
import GradientBarChart from '../gradient-bar-chart';
import * as echarts from 'echarts/core';
import { dashboardService } from '../../queries/dashboard';
import useUserMemory from '../../hooks/use-user-memory';

type DifficultyLevelProps = {
  Easy: number;
  Good: number;
  Hard: number;
  news: number;
};

const mapDifficultyLevel = (difficultyLevel: DifficultyLevelProps) => {
  return [
    {
      label: 'Fácil',
      value: difficultyLevel.Easy,
      colorFrom: '#14B8A6',
      colorTo: '#5EEAD4',
    },
    {
      label: 'Médio',
      value: difficultyLevel.Good,
      colorFrom: '#FDE68A',
      colorTo: '#FBBF24',
    },
    {
      label: 'Difícil',
      value: difficultyLevel.Hard,
      colorFrom: '#DC2626',
      colorTo: '#B91C1C',
    },
    {
      label: 'Novos',
      value: difficultyLevel.news,
      colorFrom: '#A3E635',
      colorTo: '#34D399',
    },
  ];
};

const DifficultyLevel = () => {
  const { memoryUserUuid } = useUserMemory();
  const [data, setData] = useState<DifficultyLevelProps>({
    Easy: 0,
    Good: 0,
    Hard: 0,
    news: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!memoryUserUuid) return;

      const response =
        await dashboardService.getDifficultyLevel(memoryUserUuid);
      setData(response);
    };

    fetchData();
  }, [memoryUserUuid]);

  const isNotData = useMemo(() => {
    return Object.values(data).every((value) => value === 0);
  }, [data]);

  const items = useMemo(() => {
    const response = mapDifficultyLevel(data);

    const options = {
      tooltip: {
        show: !isNotData,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      dataset: {
        dimensions: ['label', 'Flashcards'],
        source: response.map((item) => ({
          label: item.label,
          Flashcards: isNotData ? 100 : item.value,
        })),
      },
      xAxis: {
        type: 'category',
        splitLine: false,
        axisLine: false,
      },
      yAxis: {
        type: 'value',
        splitLine: { show: false },
        axisLabel: {
          show: !isNotData,
        },
      },
      series: [
        {
          type: 'bar',
          itemStyle: {
            barBorderRadius: 50,
            color: function (params: any) {
              const label = params?.data?.label;

              const index = response.findIndex((item) => item.label === label);
              const color = response[index]?.colorFrom;
              const colorTo = response[index]?.colorTo;

              return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: isNotData ? '#E6EBF0' : color },
                { offset: 1, color: isNotData ? '#E6EBF0' : colorTo },
              ]);
            },
          },
        },
      ],
      grid: {
        width: '100%',
      },
    };

    return options;
  }, [data, isNotData]);

  return (
    <div className='flex w-full flex-col gap-2'>
      <div className='flex h-[45px] flex-row items-center justify-between gap-2'>
        <Typography className='text-h5 font-bold !text-mono-color-darker'>
          Nível de Dificuldade
        </Typography>
      </div>
      <MemoryCard className='flex h-[250px] flex-row items-center justify-center'>
        <div className='flex h-full w-full items-center justify-center'>
          <GradientBarChart options={items} />
        </div>
      </MemoryCard>
    </div>
  );
};

export default DifficultyLevel;
